import { useEffect, useMemo, useState } from 'react';
import { usePlacingPayment } from 'contexts/placingPayment';
import { plus, divide, multiply } from 'utils/number';
import { Payload } from 'baseTypes';
import { oraclePayment } from 'constants/payment';
import { requestCheckCoupon, requestClearCoupon, requestFreePayment } from 'services/discount';
import { requestApplyVoucher, requestClearVoucher, requestPayWithVoucher } from 'services/voucher';
import { SESSION } from 'constants/enum/session';
import { useTranslation } from 'react-i18next';

interface UseViewModelProps {
  total?: number;
  menuItems?: Payload;
  type?: string;
  fullAmount?: number;
  amountExcludedFee?: number;
}

const getVoucherErrorMessage = (error: any) => {
  const message = String(error?.message || error?.voucher?.message || '');
  const normalizedMessage = message.toLowerCase();

  if (normalizedMessage.includes('voucher_not_found') || normalizedMessage.includes('invalid voucher')) {
    return 'Dieser Gutschein ist ungültig.';
  }
  if (normalizedMessage.includes('voucher_expired')) {
    return 'Dieser Gutschein ist abgelaufen.';
  }
  if (normalizedMessage.includes('voucher_not_yet_valid')) {
    return 'Dieser Gutschein ist noch nicht gültig.';
  }
  if (normalizedMessage.includes('voucher_deactivated')) {
    return 'Dieser Gutschein wurde deaktiviert.';
  }
  if (normalizedMessage.includes('quota') || normalizedMessage.includes('does not contribute')) {
    return 'Dieser Gutschein kann nicht mehr verwendet werden.';
  }
  if (normalizedMessage.includes('already applied') || normalizedMessage.includes('discount is already applied')) {
    return 'Der Gutschein konnte nicht angewendet werden. Bitte versuche es erneut.';
  }

  return 'Der Gutschein konnte nicht angewendet werden. Bitte versuche es erneut.';
};

const useViewModel = ({ total, menuItems: menuItemsProp, type }: UseViewModelProps) => {
  const {
    totalTipPercent,
    placingPayment,
    paymentInfo,
    handleSuccess,
    happyHourError,
    isHappyHour,
    setIsHappyHour,
    setHappyHourError,
    error,
    setError,
    getTipPercentage,
    totalAmountExcludedFee,
  } = usePlacingPayment();
  const [code, setCode] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const token = sessionStorage?.getItem(SESSION.TOKEN) ?? '';
  const { discount, isDiscountMinimum, amount, isSplitBill } = paymentInfo;
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const { t } = useTranslation();

  const totalBill = useMemo(() => {
    return totalTipPercent > 0
      ? plus(multiply(totalAmountExcludedFee(), divide(totalTipPercent, 100)), total).toFixed(2)
      : total;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, totalTipPercent]);

  useEffect(() => {
    totalBill ? setOpenDialog(false) : setOpenDialog(true);
  }, [totalBill]);

  const parseMenuItem = (menuItem: {
    canceled?: boolean;
    name: string;
    quantity: number;
    totalPrice: number;
    unitPrice: number;
    nameOriginal?: string;
  }) => {
    return {
      canceled: menuItem?.canceled,
      quantity: menuItem.quantity,
      name: menuItem.name,
      unitPrice: menuItem.unitPrice,
      total: menuItem.totalPrice,
      nameOriginal: menuItem.nameOriginal,
    };
  };

  const parseSubMenuItem = (subMenuItem: {
    name: string;
    quantity: number;
    totalPrice: number;
    unitPrice: number;
    title: string;
    nameOriginal?: string;
    titleOriginal?: string;
  }) => {
    return {
      name: subMenuItem.name,
      quantity: subMenuItem.quantity,
      total: subMenuItem.totalPrice,
      unitPrice: subMenuItem.unitPrice,
      title: subMenuItem.title,
      nameOriginal: subMenuItem.nameOriginal,
      titleOriginal: subMenuItem.titleOriginal,
    };
  };

  const menuItems = useMemo(() => {
    if (type !== oraclePayment) {
      return (
        menuItemsProp?.checkoutItems?.map((item) => {
          return {
            ...parseMenuItem(item),
            subItems: item.extra?.map((extraChild) => {
              return parseSubMenuItem(extraChild);
            }),
          };
        }) ?? []
      );
    } else {
      return (
        menuItemsProp?.billConfig?.menuItems.map((item) => {
          return {
            ...parseMenuItem({
              ...item,
              quantity: item.quantity ?? 0,
              totalPrice: item.total ?? 0,
            }),
            subItems: item?.condiments?.map((condiment) => {
              return parseSubMenuItem({
                ...condiment,
                totalPrice: condiment.total,
              });
            }),
          };
        }) ?? []
      );
    }
  }, [menuItemsProp, type]);

  const checkCoupon = (code: string) => {
    if (code) {
      setLoading(true);
      const codeLowerCase = code.toLowerCase();
      requestCheckCoupon(token, codeLowerCase)
        .then(() => {
          setIsHappyHour(false);
          setCode(code);
          setError('');
          return placingPayment();
        })
        .catch((error) => {
          setError(error?.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const clearCoupon = () => {
    setLoading(true);
    requestClearCoupon(token)
      .then(() => {
        setCode('');
        setError('');
        setHappyHourError('');
        setIsHappyHour(false);
        return placingPayment();
      })
      .catch(({ messageKey }) => {
        setError(t(`${messageKey}`));
      })
      .finally(() => {
        setLoading(false);
        setOpenDialog(false);
      });
  };

  const discountZeroSuccess = () => {
    setLoading(true);
    requestFreePayment(token, getTipPercentage())
      .then(() => {
        handleSuccess();
      })
      .catch(({ messageKey }) => {
        setError(t(`${messageKey}`));
      })
      .finally(() => {
        setLoading(false);
        setOpenDialog(false);
      });
  };

  // Voucher state
  const [voucherCode, setVoucherCode] = useState<string>('');
  const [voucherError, setVoucherError] = useState<string>('');
  const [voucherLoading, setVoucherLoading] = useState<boolean>(false);
  const [voucherDiscount, setVoucherDiscount] = useState<number>(0);
  const [voucherCoversFullAmount, setVoucherCoversFullAmount] = useState<boolean>(false);

  const applyVoucher = (inputCode: string) => {
    if (!inputCode) return;
    setVoucherLoading(true);
    setVoucherError('');
    requestApplyVoucher(token, inputCode)
      .then((res) => {
        setVoucherCode(res.voucher.code);
        setVoucherDiscount(res.voucher.discountAmount);
        setVoucherCoversFullAmount(res.voucher.coversFullAmount);
        setVoucherError('');
        return placingPayment();
      })
      .catch((err) => {
        setVoucherError(getVoucherErrorMessage(err));
      })
      .finally(() => {
        setVoucherLoading(false);
      });
  };

  const clearVoucher = () => {
    setVoucherLoading(true);
    requestClearVoucher(token)
      .then(() => {
        setVoucherCode('');
        setVoucherDiscount(0);
        setVoucherCoversFullAmount(false);
        setVoucherError('');
        return placingPayment();
      })
      .catch((err) => {
        setVoucherError(err?.message || 'Fehler beim Entfernen');
      })
      .finally(() => {
        setVoucherLoading(false);
      });
  };

  const payWithVoucher = () => {
    setVoucherLoading(true);
    requestPayWithVoucher(token, getTipPercentage())
      .then(() => {
        handleSuccess();
      })
      .catch((err) => {
        setVoucherError(err?.message || 'Fehler bei der Gutschein-Zahlung');
      })
      .finally(() => {
        setVoucherLoading(false);
      });
  };

  return {
    totalBill,
    totalTipPercent,
    menuItems,
    code,
    setCode,
    token,
    error,
    setError,
    checkCoupon,
    clearCoupon,
    discount,
    loading,
    isDiscountMinimum,
    isHappyHour,
    amount,
    openDialog,
    setOpenDialog,
    discountZeroSuccess,
    happyHourError,
    totalAmountExcludedFee,
    isSplitBill,
    voucherCode,
    voucherError,
    voucherLoading,
    voucherDiscount,
    voucherCoversFullAmount,
    applyVoucher,
    clearVoucher,
    payWithVoucher,
  };
};

export default useViewModel;
